import axios from 'axios';
import ServicePaths from '../bridge/utils/ServicePaths';
import Services from '../bridge/utils/Services';
import {
  WebsocketMessageType,
  WebsocketMessages,
} from '../bridge/utils/WebsocketMessages';
import { useCallback, useEffect, useState } from 'react';
import useWebsocket from 'react-use-websocket';
import LogRecord, { LogRecordType } from '../bridge/utils/LogRecord';

const getStartWebsocketServicePath = (
  service: Services | undefined,
  botActivePort: number | undefined,
) => {
  if (!!botActivePort) {
    return ServicePaths.BOT.StartLogsWebsocket(botActivePort);
  }

  switch (service) {
    case Services.WebClient:
    case Services.ProfitCalculations:
      return null;

    case Services.Data:
      return ServicePaths.DATA.StartLogsWebsocket;

    case Services.CandleFetcher:
      return ServicePaths.CANDLE_FETCHER.StartLogsWebsocket;

    case Services.Control:
      return ServicePaths.CONTROL.StartLogsWebsocket;

    case Services.Order:
      return ServicePaths.ORDER.StartLogsWebsocket;

    case Services.Exchange:
      return ServicePaths.EXCHANGE.StartLogsWebsocket;
  }

  return null;
};

const getWebsocketServicePath = (
  service: Services | undefined,
  botActivePort: number | undefined,
) => {
  if (!!botActivePort) {
    return ServicePaths.BOT.Websocket(botActivePort);
  }

  switch (service) {
    case Services.WebClient:
    case Services.ProfitCalculations:
      return null;

    case Services.Data:
      return ServicePaths.DATA.Websocket;

    case Services.CandleFetcher:
      return ServicePaths.CANDLE_FETCHER.Websocket;

    case Services.Control:
      return ServicePaths.CONTROL.Websocket;

    case Services.Order:
      return ServicePaths.ORDER.Websocket;

    case Services.Exchange:
      return ServicePaths.EXCHANGE.Websocket;
  }

  return null;
};

/**
 * We receive the service to connect to
 * We can get the given port of the service
 * First we want to enable the websocket since this doesn't run automatically
 * When we get result true back, we can start connecting to the websocket
 *
 * For now we only want to get a list of the logs,
 * so that's the only thing that needs to be returned
 *
 * We can receive two different websocket messages at the moment
 * allLogs, if we get this, we want to set the entire logs array
 * singleLog, if we get this, we want to append it to the end
 */
const useLogsWebsocket = (
  service: Services | undefined,
  botActivePort: number | undefined,
  logRecordType?: LogRecordType,
  searchQuery?: string | undefined,
  startTimestamp?: string | undefined,
  endTimestamp?: string | undefined,
) => {
  const [shouldConnectToWebsocket, setShouldConnectToWebsocket] =
    useState(false);
  const [logRecords, setLogRecords] = useState<LogRecord[]>([]);
  const [isOpenWebsocketLoading, setIsOpenWebsocketLoading] = useState(true);
  const [hasErrored, setHasErrored] = useState(false);

  useEffect(() => {
    // Initially disable the websocket when changing services
    setShouldConnectToWebsocket(false);
    setIsOpenWebsocketLoading(true);
    setLogRecords([]);
  }, [service, botActivePort]);

  const websocketServicePath = getWebsocketServicePath(service, botActivePort);
  const { lastMessage, readyState, getWebSocket, sendJsonMessage } =
    useWebsocket(
      websocketServicePath,
      undefined,
      shouldConnectToWebsocket && !!websocketServicePath,
    );

  /** Enable the websocket */
  useEffect(() => {
    const enableWebsocket = async () => {
      try {
        const startWebsocketServicePath = getStartWebsocketServicePath(
          service,
          botActivePort,
        );

        if (!startWebsocketServicePath) {
          return;
        }

        const {
          data: { success },
        } = await axios({
          method: 'POST',
          url: startWebsocketServicePath,
        });

        setShouldConnectToWebsocket(success);
        setIsOpenWebsocketLoading(false);
      } catch (error) {
        console.error(error);
        setHasErrored(true);
      }
    };

    enableWebsocket();
  }, [service, botActivePort]);

  useEffect(() => {
    return () => {
      const ws = getWebSocket();

      if (ws && (ws.OPEN || ws?.CONNECTING)) {
        ws.close();
      }
    };
  }, [service, getWebSocket]);

  const isWebsocketLoading = readyState === -1 || readyState === 0;

  const handleWebsocketMessage = useCallback(
    (lastMessage: MessageEvent<string>) => {
      const data = JSON.parse(lastMessage.data) as WebsocketMessages;
      const messageType = data.type;

      if (messageType === WebsocketMessageType.AllLogs) {
        if (!!logRecordType) {
          setLogRecords(
            data.logRecords.filter((r) => r.type === logRecordType),
          );
          return;
        }

        setLogRecords(data.logRecords);
        return;
      }

      if (messageType === WebsocketMessageType.SingleLog) {
        if (!!logRecordType) {
          if (data.logRecord.type === logRecordType) {
            setLogRecords((prev) => [...prev, data.logRecord]);
            return;
          }

          return;
        }

        setLogRecords((prev) => [...prev, data.logRecord]);
        return;
      }
    },
    [logRecordType],
  );

  useEffect(() => {
    if (!lastMessage) {
      return;
    }

    handleWebsocketMessage(lastMessage);
  }, [lastMessage, handleWebsocketMessage]);

  // Close on dismount
  useEffect(() => {
    return () => {
      const ws = getWebSocket();

      if (ws && (ws.OPEN || ws?.CONNECTING)) {
        ws.close();
      }
    };
  }, [getWebSocket]);

  useEffect(() => {
    if (
      searchQuery ||
      (startTimestamp && startTimestamp.length === 13) ||
      (endTimestamp && endTimestamp.length === 13)
    ) {
      sendJsonMessage({
        type: 'fetch',
        searchQuery,
        startTimestamp:
          startTimestamp && startTimestamp.length === 13
            ? parseInt(startTimestamp)
            : undefined,
        endTimestamp:
          endTimestamp && endTimestamp.length === 13
            ? parseInt(endTimestamp)
            : undefined,
      });
    }
  }, [searchQuery, startTimestamp, endTimestamp, sendJsonMessage]);

  return {
    logRecords,
    isLoading:
      isOpenWebsocketLoading ||
      isWebsocketLoading ||
      (!logRecordType && logRecords.length < 2),
    hasErrored,
  };
};

export default useLogsWebsocket;
